import { api } from './api'
import { renderFollowup, type FollowupEntity, type FollowupHandle, type FollowupRow } from './followup'
import { openMessagesFor } from './screens/rebook'
import { el } from './ui'

let openDialog: HTMLDialogElement | null = null

// Lives outside the screen so a background refresh cannot erase a contact draft.
// send: false opens just the form, as "Contact follow-up" (the customer page's ⋯, whose own Message button already
// goes straight to their message; customer page redesign, Liam 9 Oct 2026). Work and New jobs keep both halves.
export function openFollowupDialog(entity: FollowupEntity, record: FollowupRow, opts: { send?: boolean } = {}): void {
  const withSend = opts.send !== false
  if (openDialog) { openDialog.focus(); return }
  const previous = document.activeElement as HTMLElement | null
  const dialog = el('dialog', { class: 'booking-dialog', 'aria-labelledby': 'followup-title' })
  openDialog = dialog
  const title = el('h2', { id: 'followup-title' }, `${withSend ? 'Message' : 'Contact follow-up'} · ${record.name}`)
  const close = el('button', { type: 'button', 'aria-label': 'Close' }, 'Close')
  const content = el('div', { class: 'booking-content' }, el('p', {}, 'Loading current details…'))
  // Its own row, not inside `content`: the load below clears content to render
  // the form, and a send button that vanished on load would be worse than none.
  // Send pinned to the foot (pin-foot.ts, 26 Oct 2026), below the form.
  const sendRow = el('footer', { class: 'followup-send pin-foot' })
  dialog.append(el('header', { class: 'booking-head' }, title, close), content, sendRow)
  let handle: FollowupHandle | undefined
  const canClose = (): boolean => !handle?.isSaving() && (!handle?.isDirty() || window.confirm('Close this draft? Any changes already queued will still save. Unsubmitted edits will be lost.'))
  close.addEventListener('click', () => { if (canClose()) dialog.close() })
  // Send, or write down what was said — the two halves of getting in touch,
  // behind one button on every row (Liam 2026-09-16). Sending stays on the
  // Messages screen: the words, the day offered and the count of what has gone
  // are decided in ONE place, and this takes the owner straight to that
  // person's card instead of making them search for it.
  const send = el('button', { type: 'button', class: 'btn-primary' }, 'Send them a message')
  send.addEventListener('click', () => {
    if (!canClose()) return
    const who = { id: record.id, name: record.name }
    dialog.close()
    openMessagesFor(who, entity === 'leads' ? 'lead' : 'customer')
  })
  if (withSend) sendRow.append(send, el('p', { class: 'muted' }, 'Opens their message on the Messages screen, with the wording and the day to offer them.'))
  else sendRow.hidden = true
  dialog.addEventListener('cancel', e => { if (!canClose()) e.preventDefault() })
  dialog.addEventListener('close', () => { dialog.remove(); openDialog = null; if (previous?.isConnected) previous.focus() })
  document.body.append(dialog); dialog.showModal()
  void (async () => {
    let current = record
    let warning = ''
    try {
      const response = await api<{ customer?: FollowupRow; lead?: FollowupRow }>(`/api/${entity}/${record.id}`)
      const fetched = entity === 'customers' ? response.customer : response.lead
      if (!fetched || fetched.id !== record.id) throw new Error('Record could not be refreshed')
      current = fetched
    } catch {
      warning = 'Showing the details already loaded. Saving will check whether this record has changed.'
    }
    if (!dialog.isConnected) return
    content.textContent = ''
    if (warning) content.append(el('p', { class: 'muted' }, warning))
    const host = el('div'); content.append(host)
    handle = renderFollowup(host, entity, current, undefined, state => { close.disabled = state.saving })
  })()
}
